import type { CountingProblem } from '../../types/counting';
import type { UseTrickAnimationReturn } from './useTrickAnimation';
import { PlayableHandDisplay } from './PlayableHandDisplay';
import { PlayedCardsDisplay } from './PlayedCardsDisplay';
import { TrickCountIndicator } from './TrickCountIndicator';
import { TrickNavigationBar } from './TrickNavigationBar';
import { AnimationSpeedControl } from './AnimationSpeedControl';
import { InitialPlayButton } from './InitialPlayButton';
import { ClaimedIndicator } from './ClaimedIndicator';
import { DIRECTION_FULL_NAMES } from './directionNames';
import { AuctionPanel } from '../table-session/AuctionPanel';
import { VulnerabilityBoard } from '../table-session/VulnerabilityBoard';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import type { Position } from '../../types/problems';

/**
 * The Counting play table, laid out as a picture of a bridge table (client redesign, 2026-09-02).
 *
 * The rebuilt page's table had been arrived at by six rounds of local corrections, and it showed:
 * the board floating in a tall empty cell, the auction stranded over one hand, the trick count
 * bolted under whatever happened to be above it, and a bottom row of three separately bordered
 * boxes at three different heights. This is the same information in three full-width bands:
 *
 *   THE RAIL    - the vulnerability board with the contract-and-tricks indicator under it.
 *                 Everything that is ABOUT the deal rather than part of the play, in one column.
 *   THE AUCTION - beside the rail on phones and tablets; on desktop it is NOT here at all - the
 *                 page hands it to the frame, which draws it in a panel above "Your Answer".
 *   THE TABLE   - the two visible hands on their own compass sides around the played trick, which
 *                 sits on green baize. East/West read left/right, North/South above/below, and a
 *                 North/South hand lays its four suits out in ONE row so the stack stays short.
 *   CONTROLS    - one strip: trick navigation, the declarer, the speed slider. Grouped and sharing
 *                 a single centre line, rather than three separately bordered boxes.
 *
 * One DOM; index.css places these with grid areas per band under `.counting-table`. The one
 * arrangement difference is the phone, where a North/South pair goes BESIDE the trick rather than
 * above and below it - stacked, the pair needs more than the 213px a 360x640 phone gives the whole
 * panel. CSS `order` puts the declarer's hand first there while the DOM keeps compass order for a
 * screen reader.
 *
 * Presentation only: every value it draws is computed by `PlayTable` and passed in, and the
 * animation is owned by the page. Rendered ONLY for `variant="table-session"` - /howard-hands,
 * /ask-jeff-review keep the pre-028 table untouched.
 */

export interface CountingTableSeat {
  hand: { spades: string; hearts: string; diamonds: string; clubs: string };
  /** "You hold" / "Dummy" plus the seat, spelled out. */
  label: string;
  /** The same, with the seat as a letter - what a portrait phone has room for. */
  mobileLabel: string;
  direction: string;
  role: 'declarer' | 'dummy';
}

export interface CountingTableProps {
  problem: CountingProblem;
  animation: UseTrickAnimationReturn;
  /** The two visible hands in COMPASS order - [W, E] or [N, S]. */
  seats: [CountingTableSeat | null, CountingTableSeat | null];
  declarerPlayedCards: string[];
  dummyPlayedCards: string[];
  nsTricksWon: number;
  ewTricksWon: number;
  canGoPrevTrick: boolean;
  canGoPrevCard: boolean;
  canGoNextCard: boolean;
  canGoNextTrick: boolean;
  onPlayStop: () => void;
  showClaimed: boolean;
  /**
   * Whether the auction is drawn INSIDE the table. Phones and tablets say yes; on desktop the page
   * gives it to the frame's own auction panel, above the answer, and passes `false` here.
   */
  showAuction: boolean;
}

export function CountingTable({
  problem,
  animation,
  seats,
  declarerPlayedCards,
  dummyPlayedCards,
  nsTricksWon,
  ewTricksWon,
  canGoPrevTrick,
  canGoPrevCard,
  canGoNextCard,
  canGoNextTrick,
  onPlayStop,
  showClaimed,
  showAuction,
}: CountingTableProps) {
  const isNSAxis = problem.declarerDirection === 'N' || problem.declarerDirection === 'S';
  // The only thing the layout asks JavaScript: which spelling of the hand labels fits. Everything
  // else about the arrangement is CSS, so a resize needs no re-render.
  const isPhone = useMediaQuery('(max-width: 767px)');

  const renderSeat = (seat: CountingTableSeat | null) => {
    if (!seat) return null;
    return (
      <div
        key={seat.direction}
        className="counting-seat"
        data-role={seat.role}
        data-seat={seat.direction}
      >
        <PlayableHandDisplay
          hand={seat.hand}
          playedCards={seat.role === 'declarer' ? declarerPlayedCards : dummyPlayedCards}
          label={isPhone ? seat.mobileLabel : seat.label}
          stretch
          removePlayedCards
        />
      </div>
    );
  };

  return (
    <div className="counting-table" data-axis={isNSAxis ? 'ns' : 'ew'} data-testid="counting-table">
      {/* The rail: the vulnerability board with the contract-and-tricks indicator directly under
          it. Everything ABOUT the deal, in one column, so the play area beside it is uninterrupted.
          Desktop centres it beside the table; the narrower widths put it top-left with the auction
          beside it, where stacking the two is what gives the auction the width it needs (client,
          2026-09-02: the auction was scrolling sideways with the count in its row). */}
      <div className="counting-rail">
        <VulnerabilityBoard
          vulnerability={problem.vulnerability}
          decisionSeat={problem.declarerDirection as Position}
          dealer={problem.dealDirection as Position}
        />
        <TrickCountIndicator
          nsTricksWon={nsTricksWon}
          ewTricksWon={ewTricksWon}
          contract={problem.contract}
          contractDirection={problem.contractDirection}
        />
      </div>

      {/* The auction, INSIDE the table - phones and tablets only. On desktop the page hands it to
          the frame instead, where it gets a panel of its own above "Your Answer" (client,
          2026-09-02): that column had the room and the table did not. `showAuction` is the page's
          word on which of the two it is. */}
      {showAuction && (
        <div className="counting-auction">
          {/* Matches TableFrame's PanelHeading exactly - 12px floor, sentence case on phones - so
              this box reads as one of the frame's panels rather than as a box of its own. */}
          <h2 className="counting-auction-title text-xs font-semibold uppercase tracking-[0.09em] text-ink-soft narrow:normal-case narrow:tracking-normal">
            The Auction
          </h2>
          <AuctionPanel
            auction={problem.auction}
            vulnerability={problem.vulnerability}
            decisionSeat={problem.declarerDirection}
            // The auction is COMPLETE here - the hand was bid and played, and the learner is
            // counting the play rather than choosing a call. There is no pending seat to mark.
            pendingSeat={null}
            rewindOn={[problem.tableResultId]}
          />
        </div>
      )}

      {/* Band 2: the table itself.
          The seats are in COMPASS order in the DOM (West then East, North then South), whatever
          the arrangement: a screen reader reads the table the way it is laid out, and the phone's
          declarer-first order is done with CSS `order` so it costs the reading order nothing. */}
      <div className="counting-baize-row">
        {renderSeat(seats[0])}

        <div className="counting-baize">
          <div className="counting-baize-felt">
            <PlayedCardsDisplay trickCards={animation.currentTrickCards} />
            {animation.animationState === 'INITIAL' && (
              <InitialPlayButton onClick={animation.startInitial} />
            )}
            {/* The middle of the baize is the one spot every viewport leaves empty, and the badge
                is a fact about the trick it sits on. It used to ride under the hands, where it
                pushed the layout down by a line on exactly the hands that needed the room.

                Not on a PHONE (client report, 2026-09-03): the trick there is ~110px wide, so the
                pill covered the cards and there is nothing to dismiss it with. Phones show it in
                the frame's masthead instead, in the declarer's place — which is where the pre-028
                page put it — so the two are exact complements and it shows exactly once. */}
            {showClaimed && (
              <span className="counting-claimed-pill narrow:hidden">
                <ClaimedIndicator />
              </span>
            )}
          </div>
        </div>

        {renderSeat(seats[1])}
      </div>

      {/* Band 3: the controls.
          One strip, three items on a single centre line. The declarer sits between them rather
          than under the cards: the nav bar and the slider are both compact and left a gap in the
          middle of the strip, and it is the fact that says whose play is being counted. */}
      <div className="counting-controls-bar">
        <TrickNavigationBar
          animationState={animation.animationState}
          canGoPrevTrick={canGoPrevTrick}
          canGoPrevCard={canGoPrevCard}
          canGoNextCard={canGoNextCard}
          canGoNextTrick={canGoNextTrick}
          onPrevTrick={animation.prevTrick}
          onPrevCard={animation.prevCard}
          onPlayStop={onPlayStop}
          onNextCard={animation.nextCard}
          onNextTrick={animation.nextTrick}
          disabled={animation.animationState === 'INITIAL'}
          hasReachedStopTrick={animation.hasReachedStopTrick}
        />

        {/* Phones drop it: the strip there carries five buttons and a slider in ~330px, and both
            hand labels already name their seat ("You hold (S)"), as does the contract chip. */}
        <span className="counting-declarer hidden md:inline narrow:hidden">
          Declarer:{' '}
          <b>{DIRECTION_FULL_NAMES[problem.declarerDirection] ?? problem.declarerDirection}</b>
        </span>

        <AnimationSpeedControl speed={animation.speed} onSpeedChange={animation.setSpeed} />
      </div>
    </div>
  );
}
